/**
 * 작업 입실·퇴실 (2026-10-27 사용자 결정 「작업 입실 등록」) — 데이터 훅.
 *
 * 시스템은 누가 시설 안에 있는지 모른다(협약: 위치 미추적). 그래서 출입구 밖 현장 PC(매니져)의
 * 「진입 전 확인」 화면에서 작업자가 [작업 입실]을 누르고 인원을 적으면 입실 기록 한 건이 생기고,
 * 나올 때 현장 PC 또는 앱·웹의 [작업 완료 퇴실]로 닫는다. 열린 기록의 인원 합이 「작업 입실 중」이다 —
 * **출입 기록이지 위치가 아니다.** 퇴실을 잊은 기록은 서버가 저절로 닫지 않고 12시간이 넘으면
 * `long_open`(퇴실 미처리 의심)으로 표시만 한다.
 *
 *   GET  /evacuation/work-entries?site=<id>[&all=1] → {work_inside, recent?}   (현장 접근 가능한 조직원 누구나)
 *   GET  /evacuation/work-entries?site=<id>&days=N  → {work_inside, history: {days, entries, truncated}}  (기록 화면, 백엔드 3.0.49~)
 *   POST /evacuation/work-entries/<id>/exit  {}      → {entry, changed, work_inside}  (두 번 눌러도 한 번)
 *   WS   site:<id>:evac  `evacuation.work`           → {site_id, work_inside}
 *   대피 현황(`/evacuation/status`)에도 같은 `work_inside` 가 실린다(관제 역할).
 *
 * 옛 서버(백엔드 3.0.48 전)는 이 경로가 없어 404 다 — 화면은 아무것도 그리지 않고, 10분마다 다시 두드려
 * 서버가 올라가면 새로고침 없이 넘어간다(risk `useRiskLatest` 와 같은 방식). WS 구독도 조회가 성공한 뒤에만 연다.
 */

import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { api } from "../client";
import { qk } from "../keys";
import { useEvacChannel } from "../ws";
import type { EvacuationStatus } from "./evacuation";

// ── Server shapes (hand-typed; not in the generated schema yet) ─────────────
export interface WorkEntry {
  id: number;
  client_id: string;
  site_id: number;
  /** 입실 인원(현장 PC 에서 적은 수, 1~99). */
  headcount: number;
  note: string;
  /** 현장 PC 「진입 전 확인」 판정 — 진입 가능(safe)·주의(caution)일 때만 입실을 받는다. */
  entry_verdict: "safe" | "caution";
  entered_at: string;
  entered_via: "manager" | "app";
  exited_at: string | null;
  exited_via: "manager" | "app" | "" | null;
  /** 입실을 기록한 현장 PC 이름(백엔드 3.0.49~ — 옛 서버는 없음). */
  manager_name?: string;
  /** 앱·웹에서 퇴실을 누른 사람(현장 PC 퇴실·옛 서버는 빈 값). */
  exited_by_name?: string;
  open: boolean;
  /** 12시간 넘게 열린 입실 — 「퇴실 미처리 의심」. 서버는 닫지 않는다. */
  long_open: boolean;
}

/** 「작업 입실 중」 — 열린 입실 기록과 인원 합. */
export interface WorkInside {
  headcount: number;
  count: number;
  entries: WorkEntry[];
}

/** 기록 화면 — 최근 `days` 일 입실 + 기간 밖이어도 아직 열린 입실, 새 것부터. 넘치면 `truncated`. */
export interface WorkEntryHistory {
  days: number;
  entries: WorkEntry[];
  truncated: boolean;
}

export interface WorkEntriesResponse {
  work_inside: WorkInside;
  /** `all=1` 일 때만 — 닫힌 행 포함 최근 20건. */
  recent?: WorkEntry[];
  /** `days=N` 일 때만(백엔드 3.0.49~). 옛 서버는 키가 없다. */
  history?: WorkEntryHistory;
}

export interface WorkEntryExitResult {
  entry: WorkEntry;
  /** false = 이미 닫힌 기록(현장 PC·다른 사람이 먼저 퇴실). */
  changed: boolean;
  work_inside: WorkInside;
}

/** WS `evacuation.work` 프레임. */
export interface EvacuationWorkPayload {
  site_id: number;
  work_inside: WorkInside;
}

const httpStatus = (err: unknown) => (err as { status?: number } | null)?.status;
const isNotFound = (err: unknown) => httpStatus(err) === 404;

/** 응답에서 `work_inside` 를 꺼낸다 — 옛 서버(키 없음)·모양이 다르면 null(화면은 아무것도 안 그린다). */
export function workInsideOf(x: { work_inside?: WorkInside | null } | null | undefined): WorkInside | null {
  const w = x?.work_inside;
  return w && typeof w.headcount === "number" && Array.isArray(w.entries) ? w : null;
}

/** 퇴실 미처리 의심(12시간 넘게 열린) 기록 수. */
export function longOpenCount(w: WorkInside | null | undefined): number {
  return w ? w.entries.filter((e) => e.long_open).length : 0;
}

/**
 * 새 `work_inside` 를 이 현장의 캐시 두 곳(입실 목록·대피 현황)에 바로 반영한다.
 * 대피 현황은 이미 받아 둔 것만 고친다 — 없는 캐시를 만들면 관제 역할이 아닌 작업자 화면에 반쪽 현황이 생긴다.
 * `all=1` 목록의 `recent` 는 여기서 맞출 수 없어 무효화로 다시 받는다.
 */
export function applyWorkInside(qc: QueryClient, siteId: number, w: WorkInside) {
  qc.setQueryData<WorkEntriesResponse>(qk.safety.evacuation.workEntries.site(siteId), (prev) => ({
    ...(prev ?? {}),
    work_inside: w,
  }));
  qc.setQueryData<EvacuationStatus>(qk.safety.evacuation.status(siteId), (prev) =>
    prev ? { ...prev, work_inside: w } : prev,
  );
  void qc.invalidateQueries({ queryKey: qk.safety.evacuation.workEntries.site(siteId, true) });
  // 기록 화면 — 입실·퇴실이 생기면 표도 다시 받는다(`history` 의 앞 다섯 칸으로 기간과 상관없이).
  void qc.invalidateQueries({ queryKey: qk.safety.evacuation.workEntries.history(siteId).slice(0, 5) });
}

// ── Hooks ───────────────────────────────────────────────────────────────────

/**
 * 현장의 「작업 입실 중」. 30초 폴링이 바탕이고, 조회가 되는 서버면 evac 소켓의 `evacuation.work` 로 즉시 바뀐다.
 * 소켓이 끊겼다 다시 붙으면 REST 로 한 번 다시 맞춘다 — 끊긴 사이 퇴실 프레임을 놓쳤을 수 있다.
 */
export function useWorkEntries(siteId: number | undefined, opts?: { all?: boolean }) {
  const qc = useQueryClient();
  const all = !!opts?.all;
  const q = useQuery({
    queryKey: qk.safety.evacuation.workEntries.site(siteId, all),
    queryFn: () =>
      api
        .get<WorkEntriesResponse>("/evacuation/work-entries", {
          params: { site: siteId, ...(all ? { all: 1 } : {}) },
        })
        .then((r) => r.data),
    enabled: !!siteId,
    refetchInterval: (query) => (isNotFound(query.state.error) ? 600_000 : 30_000),
    retry: (n, err: unknown) => n < 1 && !isNotFound(err),
  });

  // 옛 서버(404)·접근 불가 현장이면 소켓을 열지 않는다. 같은 현장의 evac 소켓은 대피 현황 패널과 한 연결을 나눠 쓴다.
  const live = q.data && siteId ? siteId : null;
  const wsStatus = useEvacChannel<EvacuationWorkPayload>(live, (env) => {
    if (env.type !== "evacuation.work" || !live) return;
    const w = workInsideOf(env.payload);
    if (w && (env.payload?.site_id == null || env.payload.site_id === live)) applyWorkInside(qc, live, w);
  });
  const wasOpen = useRef(false);
  const refetch = q.refetch;
  useEffect(() => {
    if (wsStatus !== "open") return;
    if (wasOpen.current) void refetch();
    wasOpen.current = true;
  }, [wsStatus, refetch]);
  useEffect(() => {
    wasOpen.current = false;
  }, [live]);

  return q;
}

/**
 * 기록 화면(「작업 입·퇴실 기록」, 2026-10-27 사용자 「작업자 입/퇴실 기록 확인 화면으로 구현하는게 좋겠어」) — 최근 `days` 일.
 * 30초 폴링 + 입실·퇴실 소켓이 오면 `applyWorkInside` 가 무효화한다(소켓 구독은 같은 화면의 `useWorkEntries` 몫).
 * 옛 서버(3.0.48)는 `days` 를 모르고 `history` 없이 답한다 → 화면이 「서버 업데이트 필요」를 띄운다.
 */
export function useWorkEntryHistory(siteId: number | undefined, days: number) {
  return useQuery({
    queryKey: qk.safety.evacuation.workEntries.history(siteId, days),
    queryFn: () =>
      api
        .get<WorkEntriesResponse>("/evacuation/work-entries", { params: { site: siteId, days } })
        .then((r) => r.data),
    enabled: !!siteId,
    refetchInterval: (query) => (isNotFound(query.state.error) ? 600_000 : 30_000),
    retry: (n, err: unknown) => n < 1 && !isNotFound(err),
  });
}

/** [작업 완료 퇴실] — 응답의 `work_inside` 로 두 캐시를 바로 고치고, 대피 현황·입실 목록을 다시 받는다. */
export function useExitWorkEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (entry: Pick<WorkEntry, "id" | "site_id">) =>
      api.post<WorkEntryExitResult>(`/evacuation/work-entries/${entry.id}/exit`, {}).then((r) => r.data),
    onSuccess: (data, entry) => {
      const siteId = data.entry?.site_id ?? entry.site_id;
      const w = workInsideOf(data);
      if (w) applyWorkInside(qc, siteId, w);
      void qc.invalidateQueries({ queryKey: qk.safety.evacuation.workEntries.all });
      void qc.invalidateQueries({ queryKey: qk.safety.evacuation.status(siteId) });
    },
    onError: (_e, entry) => {
      // 404 = 이미 정리됐거나 볼 수 없는 기록 — 목록을 다시 받아 화면에서 내린다.
      void qc.invalidateQueries({ queryKey: qk.safety.evacuation.workEntries.site(entry.site_id) });
    },
  });
}
